
import React from 'react';
import SEO from '../components/SEO';
import { MODULES } from '../constants';
import GlowCard from '../components/GlowCard';
import AIImage from '../components/AIImage';
import FAQSection from '../components/FAQSection';

const PansOpsPage: React.FC = () => {
  const moduleData = MODULES.find(m => m.id === 'pans-ops')!;

  const pansOpsFAQ = [
    {
      question: "Does the tool support PANS-OPS obstacle assessment surfaces (OAS)?",
      answer: "Yes, the engine supports construction of OAS, CRM, and OLS surfaces based on the latest ICAO Doc 8168 parameters, subject to human review and approval."
    },
    {
      question: "Can we import high-resolution terrain data?",
      answer: "Absolutely. AviaGov supports GeoTIFF, DTED, and other standard terrain formats to ensure precise clearance calculations."
    },
    {
      question: "Is the final procedure design exported to eAIP?",
      answer: "Yes, designs can be exported as AIXM 5.1 features, which flow directly into our eAIP module for publication."
    }
  ];

  return (
    <div className="animate-fade-in-up">
      <SEO
        title="PANS-OPS Procedure Design"
        description="Enterprise flight procedure design and obstacle assessment software."
        ogType="product"
        faq={pansOpsFAQ}
      />

      <section className="py-24 bg-slate-950 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="order-2 lg:order-1">
             <AIImage 
               prompt="A 3D cartoon render of a flight path designer with curved neon lines around a stylized 3D airport, soft shadows, high precision."
               alt="PANS-OPS Design Interface"
               className="w-full aspect-square"
               aspectRatio="1:1"
             />
          </div>
          <div className="order-1 lg:order-2">
            <h1 className="text-5xl lg:text-7xl font-bold font-heading mb-8 leading-tight">
              Precision <br />
              <span className="text-accent">Design.</span>
            </h1>
            <p className="text-xl text-slate-400 mb-10 leading-relaxed">
              Engineered for procedure designers. Manage SID, STAR, and instrument approaches with controlled surface construction workflows and deep terrain analysis under human responsibility.
            </p>
            <p className="text-sm text-slate-500 mb-4 leading-relaxed">
              Designed for Civil Aviation Authorities, ANSPs, and Airports supporting procedure design and assessment workflows (ICAO Doc 8168).
            </p>
            <p className="text-sm text-slate-500 mb-10 leading-relaxed">
              This module supports institutional procedure environments and is not intended for airline operational use.
            </p>
            <div className="space-y-6">
               {moduleData.features.map((f, i) => (
                 <div key={i} className="flex items-start space-x-4">
                    <div className="mt-1 w-5 h-5 rounded-full bg-accent/20 flex items-center justify-center text-accent">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                         <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-white font-bold">{f.title}</h4>
                      <p className="text-slate-500 text-sm">{f.description}</p>
                    </div>
                 </div>
               ))}
            </div>
          </div>
        </div>
      </section>

      <FAQSection items={pansOpsFAQ} title="Procedure Design FAQ" />
    </div>
  );
};

export default PansOpsPage;
